import { Editor } from "@monaco-editor/react";
import { TabPanel } from "@mui/lab";
import { Box, Dialog, DialogContent } from "@mui/material";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { DiscoveryTriggerModal } from "../../components/discovery-trigger-dialog";
import { TestVersions } from "../../components/test-versions";
import { useApiQuery, usePaginatedApiQuery } from "../../hooks";
import { useGraph } from "../../hooks/use-graph";
import { useTypedQueryParams } from "../../hooks/use-query-params";
import { useUrlModal } from "../../hooks/use-url-dialog";
import type {
  Graph,
  Test,
  TestContext,
  TestMatrixOption,
  TestRunStep,
  TestVariable,
} from "../../types";
import {
  AdvancedTab,
  AnalysisTab,
  DataAssetsTab,
  DependenciesTab,
  DetailsTab,
  GraphTab,
  IntentTab,
  MatrixTab,
  RunsTab,
  TestDetailsHeader,
  TestDetailsTabs,
  VariablesTab,
  VariantsTab,
} from "./components";
import { useTestDetails, useTestMutations } from "./hooks";
import type { TestDetailsTabs as TestDetailsTabsType } from "./types";

const recursiveTestDep = (testId: string, allTests: Test[]): Test[] => {
  const test = allTests.find((t) => t.id === testId);
  if (!test || !test.test_dependency_ids || test.test_dependency_ids.length === 0) {
    return test ? [test] : [];
  }

  const dependencies = test.test_dependency_ids.flatMap((depId) =>
    recursiveTestDep(depId, allTests),
  );

  return [test, ...dependencies];
};

export default function TestDetailsPage() {
  const { testId } = useParams();

  // URL-based modal state (fixes back button behavior)
  const [versionsOpen, setVersionsOpen] = useUrlModal("versions");
  const [edgeCasesOpen, setEdgeCasesOpen] = useUrlModal("edge-cases");

  const { params: tabParams, setParam: setTabParam } = useTypedQueryParams<TestDetailsTabsType>();
  const tab = tabParams.tab ?? "intent";

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setTabParam("tab", newValue as TestDetailsTabsType["tab"]);
  };

  const { data: graphData } = useGraph();

  const {
    test,
    ctxs,
    selectedCtx,
    setSelectedCtx,
    getStepInfo,
    refetchTest,
    refetchContext,
    enqueueSnackbar: showSnackbar,
    nav: navigate,
  } = useTestDetails();

  const {
    runTest,
    isMutating,
    createContext,
    createContextAsync,
    isCreating,
    moveContext,
    isMoving,
    updateContext,
    updateContextAsync,
    isUpdatingContext,
    deleteContext,
    deleteContextAsync,
    updateTest,
    isUpdatingTest,
    isUpdatingVariables,
    duplicateTest,
    isDuplicating,
    regenerateDescription,
    isRegeneratingDescription,
    handleVariablesChange,
    isEditingTestName,
    editedTestName,
    setEditedTestName,
    startEditingTestName,
    cancelEditingTestName,
    saveTestName,
  } = useTestMutations(testId, refetchTest, refetchContext);

  const { data: tests } = useApiQuery<Test[]>("/tests");

  const {
    data: variants,
    isLoading: isLoadingVariants,
    setFilters,
  } = usePaginatedApiQuery<Test>("/tests", undefined, {
    variant_for: testId,
  });

  useEffect(() => {
    if (testId) {
      setFilters({ variant_for: testId });
    }
  }, [testId, setFilters]);

  const depGraph: Graph = useMemo(() => {
    if (!tests || !test) return { nodes: [], edges: [] };

    const depTests: Test[] = recursiveTestDep(test.id, tests);

    const nodes = depTests.map(
      (depTest) =>
        ({
          id: depTest.id,
          label: depTest.name,
          type: depTest.id === test.id ? "test_target" : "dependency",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }) as any,
    );

    const edges = depTests.flatMap((test) =>
      (test.test_dependency_ids ?? []).map((dep: string) => ({
        source: dep,
        target: test.id,
        type: "opens" as const,
      })),
    );

    return { nodes, edges };
  }, [tests, test]);

  const mutating = useMemo(() => {
    return isCreating || isMoving || isUpdatingContext;
  }, [isCreating, isMoving, isUpdatingContext]);

  // Check if there are any enabled steps
  const hasEnabledSteps = useMemo(() => {
    if (!ctxs || ctxs.length === 0) return false;
    return ctxs.some((ctx) => !ctx.disabled);
  }, [ctxs]);

  const [selectedCtxStep, setSelectedCtxStep] = useState<TestRunStep | null>(null);

  useEffect(() => {
    if (!ctxs || ctxs.length === 0 || !selectedCtx) return;

    const g = ctxs.find((ctx) => ctx.id === selectedCtx.id) || null;
    setSelectedCtx(g);
    setSelectedCtxStep(g ? getStepInfo(g) : null);
  }, [ctxs, selectedCtx, getStepInfo, setSelectedCtx]);

  // Event handlers
  const handleRunTest = () => {
    runTest({});
    setTabParam("tab", "runs");
  };

  const handleRunTestWithFix = () => {
    runTest({ allow_fix: true });
    setTabParam("tab", "runs");
  };

  const handleDuplicateTest = () => {
    duplicateTest({});
  };

  const handleViewVersions = () => {
    setVersionsOpen(true);
  };

  const handleViewEdgeCases = () => {
    setEdgeCasesOpen(true);
  };

  const handleViewOriginTest = (originTestId: string) => {
    navigate(`/tests/${originTestId}`);
  };

  const handleCopyIntent = (intent: string) => {
    navigator.clipboard.writeText(intent);
    showSnackbar("Test intent copied to clipboard!", {
      variant: "success",
      autoHideDuration: 2000,
      anchorOrigin: {
        horizontal: "right",
        vertical: "bottom",
      },
    });
  };

  const handleContextSelect = (ctx: TestContext) => {
    setSelectedCtx(ctx);
  };

  const handleContextUpdate = () => {
    refetchContext();
  };

  const handleContextDelete = (contextId: string) => {
    // Clear selection if the deleted context is currently selected
    if (selectedCtx?.id === contextId) {
      setSelectedCtx(null);
    }
    deleteContext({
      _url: `/tests/${testId}/context/${contextId}`,
    });
  };

  const handleContextCreate = (context: Partial<TestContext>) => {
    createContext(context);
  };

  const handleContextDuplicate = (contextId: string) => {
    const contextToDuplicate = ctxs?.find((ctx) => ctx.id === contextId);
    if (!contextToDuplicate) return;

    const now = new Date();
    const newContext: Partial<TestContext> = {
      name: `${contextToDuplicate.name} (copy)`,
      description: contextToDuplicate.description,
      end_goal: contextToDuplicate.end_goal,
      start_timestamp: contextToDuplicate.start_timestamp,
      end_timestamp: contextToDuplicate.end_timestamp,
      started_at: now.toISOString(),
      ended_at: now.toISOString(),
      data: contextToDuplicate.data,
      pre_conditions: contextToDuplicate.pre_conditions,
      post_conditions: contextToDuplicate.post_conditions,
      actions: contextToDuplicate.actions,
    };

    createContext(newContext);
  };

  const handleBulkDuplicate = async (contexts: TestContext[]) => {
    if (contexts.length === 0) return;

    try {
      for (const contextToDuplicate of contexts) {
        const now = new Date();
        const newContext: Partial<TestContext> = {
          name: `${contextToDuplicate.name} (copy)`,
          description: contextToDuplicate.description,
          end_goal: contextToDuplicate.end_goal,
          start_timestamp: contextToDuplicate.start_timestamp,
          end_timestamp: contextToDuplicate.end_timestamp,
          started_at: now.toISOString(),
          ended_at: now.toISOString(),
          data: contextToDuplicate.data,
          pre_conditions: contextToDuplicate.pre_conditions,
          post_conditions: contextToDuplicate.post_conditions,
          actions: contextToDuplicate.actions,
        };

        await createContextAsync(newContext);
      }

      showSnackbar(
        `Successfully duplicated ${contexts.length} step${contexts.length > 1 ? "s" : ""}`,
        {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );
      refetchContext();
    } catch (error) {
      showSnackbar(
        `Failed to duplicate steps: ${error instanceof Error ? error.message : "Unknown error"}`,
        {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );
    }
  };

  const handleBulkDelete = async (contexts: TestContext[]) => {
    if (contexts.length === 0) return;

    try {
      for (const ctx of contexts) {
        await deleteContextAsync({
          _url: `/tests/${testId}/context/${ctx.id}`,
        });
      }

      showSnackbar(
        `Successfully deleted ${contexts.length} step${contexts.length > 1 ? "s" : ""}`,
        {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );
      refetchContext();

      // Clear selection if any deleted context was selected
      if (selectedCtx && contexts.some((ctx) => ctx.id === selectedCtx.id)) {
        setSelectedCtx(null);
      }
    } catch (error) {
      showSnackbar(
        `Failed to delete steps: ${error instanceof Error ? error.message : "Unknown error"}`,
        {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );
    }
  };

  const handleBulkToggleDisable = async (contexts: TestContext[]) => {
    if (contexts.length === 0) return;

    try {
      for (const ctx of contexts) {
        await updateContextAsync({
          _url: `/tests/${testId}/context/${ctx.id}`,
          disabled: !ctx.disabled,
        });
      }

      const action = contexts.every((ctx) => ctx.disabled) ? "enabled" : "disabled";
      showSnackbar(
        `Successfully ${action} ${contexts.length} step${contexts.length > 1 ? "s" : ""}`,
        {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );
      refetchContext();
    } catch (error) {
      showSnackbar(
        `Failed to update steps: ${error instanceof Error ? error.message : "Unknown error"}`,
        {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );
    }
  };

  const handleContextMove = (contextId: string, newOrder: number) => {
    moveContext({
      _url: `/tests/${testId}/context/${contextId}/move`,
      new_order: newOrder,
    });
  };

  const handleContextToggleDisable = (contextId: string) => {
    const context = ctxs?.find((ctx) => ctx.id === contextId);
    if (!context) return;

    updateContext({
      _url: `/tests/${testId}/context/${contextId}`,
      disabled: !context.disabled,
    });
  };

  const handleAddMatrixOption = () => {
    updateTest({
      matrix: {
        ...test?.matrix,
        options: [
          ...(test?.matrix?.options ?? []),
          {
            id: crypto.randomUUID(),
            is_default: false,
            var_options: [...(test?.variables ?? [])],
          },
        ],
      },
      _url: `/tests/${test?.id}`,
    });
  };

  const handleRunMatrix = () => {
    runTest({ run_matrix: true });
  };

  const handleRunMatrixOption = (optionId: string) => {
    runTest({
      run_matrix: true,
      matrix_ids: [optionId],
    });
  };

  const handleToggleExpectedBehavior = () => {
    if (!test) return;
    updateTest({
      is_positive: test.is_positive === false,
      _url: `/tests/${test.id}`,
    });
  };

  const handleDeleteMatrixOption = (optionId: string) => {
    updateTest({
      matrix: {
        ...test?.matrix,
        options: (test?.matrix?.options ?? []).filter((o) => o.id !== optionId),
      },
      _url: `/tests/${test?.id}`,
    });
  };

  const handleUpdateMatrixOption = (optionId: string, variables: TestVariable[]) => {
    updateTest({
      matrix: {
        ...test?.matrix,
        options: (test?.matrix?.options ?? []).map((o) =>
          o.id === optionId ? { ...o, var_options: variables } : o,
        ),
      },
      _url: `/tests/${test?.id}`,
    });
  };

  const handleImportMatrixOptions = (options: TestMatrixOption[]) => {
    updateTest({
      matrix: {
        ...test?.matrix,
        options,
      },
      _url: `/tests/${test?.id}`,
    });
  };

  return (
    <Box
      sx={{
        flex: 1,
        flexDirection: "column",
        display: "flex",
        overflow: "hidden",
      }}
    >
      <TestDetailsHeader
        test={test}
        isEditingTestName={isEditingTestName}
        editedTestName={editedTestName}
        setEditedTestName={setEditedTestName}
        startEditingTestName={startEditingTestName}
        cancelEditingTestName={cancelEditingTestName}
        saveTestName={saveTestName}
        isUpdatingTest={isUpdatingTest}
        isDuplicating={isDuplicating}
        isMutating={isMutating}
        hasEnabledSteps={hasEnabledSteps}
        ctxs={ctxs}
        onRunTest={handleRunTest}
        onRunTestWithFix={handleRunTestWithFix}
        onDuplicateTest={handleDuplicateTest}
        onViewVersions={handleViewVersions}
        onViewEdgeCases={handleViewEdgeCases}
        onViewOriginTest={handleViewOriginTest}
        onToggleExpectedBehavior={handleToggleExpectedBehavior}
      />

      <DiscoveryTriggerModal
        open={edgeCasesOpen}
        onClose={() => setEdgeCasesOpen(false)}
        edgeCaseTarget={test?.id}
      />
      <Dialog open={versionsOpen} onClose={() => setVersionsOpen(false)} fullWidth maxWidth="md">
        <DialogContent>
          <TestVersions testId={testId ?? ""} />
        </DialogContent>
      </Dialog>

      <TestDetailsTabs
        tab={tab}
        onTabChange={handleChange}
        test={test}
        depGraph={depGraph}
        variants={variants ?? []}
      >
        <TabPanel value="intent">
          <IntentTab
            test={test}
            onCopyIntent={handleCopyIntent}
            onRegenerateDescription={() => regenerateDescription({})}
            isRegenerating={isRegeneratingDescription}
          />
        </TabPanel>
        <TabPanel value="details">
          {test?.variant === "pw_reporter" ? (
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                height: "100%",
                boxSizing: "border-box",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                overflow: "hidden",
              }}
            >
              <Editor
                defaultLanguage="typescript"
                height="60vh"
                value={test.template_code ?? "// No template code available."}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  lineNumbers: "on",
                  folding: false,
                  scrollBeyondLastLine: false,
                  scrollbar: {
                    vertical: "auto",
                    horizontal: "auto",
                  },
                }}
              />
            </Box>
          ) : (
            <DetailsTab
              ctxs={ctxs}
              selectedCtx={selectedCtx}
              selectedCtxStep={selectedCtxStep}
              onContextSelect={handleContextSelect}
              onContextUpdate={handleContextUpdate}
              onContextDelete={handleContextDelete}
              onContextCreate={handleContextCreate}
              onContextDuplicate={handleContextDuplicate}
              onBulkDuplicate={handleBulkDuplicate}
              onBulkDelete={handleBulkDelete}
              onBulkToggleDisable={handleBulkToggleDisable}
              onContextMove={handleContextMove}
              onContextToggleDisable={handleContextToggleDisable}
              isCreating={isCreating}
              isMoving={isMoving}
              mutating={mutating}
              getStepInfo={getStepInfo}
            />
          )}
        </TabPanel>
        <TabPanel value="variables">
          <VariablesTab
            variables={(test?.variables ?? []) as TestVariable[]}
            onVariablesChange={handleVariablesChange}
            isLoading={isUpdatingVariables}
          />
        </TabPanel>
        <TabPanel value="deps">
          <DependenciesTab
            depGraph={depGraph}
            testId={testId ?? ""}
            onDependenciesUpdate={refetchTest}
          />
        </TabPanel>
        <TabPanel value="runs">{test && <RunsTab testId={test.id} />}</TabPanel>
        <TabPanel value="data-assets">{test && <DataAssetsTab testId={test.id} />}</TabPanel>
        <TabPanel value="graph">
          <GraphTab test={test} graphData={graphData} />
        </TabPanel>
        <TabPanel value="anal">
          <AnalysisTab test={test} />
        </TabPanel>
        <TabPanel value="variants">
          <VariantsTab variants={variants} isLoading={isLoadingVariants} />
        </TabPanel>
        <TabPanel value="matrix">
          <MatrixTab
            test={test}
            isUpdatingTest={isUpdatingTest}
            isMutating={isMutating}
            onAddMatrixOption={handleAddMatrixOption}
            onRunMatrix={handleRunMatrix}
            onRunMatrixOption={handleRunMatrixOption}
            onDeleteMatrixOption={handleDeleteMatrixOption}
            onUpdateMatrixOption={handleUpdateMatrixOption}
            onImportMatrixOptions={handleImportMatrixOptions}
          />
        </TabPanel>
        <TabPanel value="advanced">
          <AdvancedTab test={test} onUpdate={updateTest} isUpdating={isUpdatingTest} />
        </TabPanel>
      </TestDetailsTabs>
    </Box>
  );
}
